import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";

// Generic admin config store keyed by config_key, scoped to org
export const useAdminConfig = (configKey: string) => {
  const { orgId } = useOrg();
  return useQuery({
    queryKey: ["admin_config", configKey, orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_config")
        .select("*")
        .eq("config_key", configKey)
        .eq("org_id", orgId)
        .maybeSingle();
      if (error) throw error;
      return data?.config_value as Record<string, unknown> | null;
    },
  });
};

export const useUpsertAdminConfig = () => {
  const { orgId } = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ config_key, config_value }: { config_key: string; config_value: Record<string, unknown> | object }) => {
      const jsonValue = JSON.parse(JSON.stringify(config_value));
      const { data, error } = await supabase
        .from("admin_config")
        .upsert(
          { config_key, config_value: jsonValue, org_id: orgId },
          { onConflict: "config_key,org_id" }
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin_config", vars.config_key, orgId] });
      toast.success("Configuration saved");
    },
    onError: (e) => toast.error(`Failed to save config: ${e.message}`),
  });
};

// Scoring weights mutation
export const useUpdateScoringWeight = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, weight }: { id: string; weight: number }) => {
      const { data, error } = await supabase.from("scoring_weights").update({ weight }).eq("id", id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scoring_weights"] });
    },
    onError: (e) => toast.error(`Failed to update weight: ${e.message}`),
  });
};

// Batch update all weights at once
export const useBatchUpdateScoringWeights = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (weights: { id: string; weight: number }[]) => {
      const results = await Promise.all(
        weights.map(({ id, weight }) =>
          supabase.from("scoring_weights").update({ weight }).eq("id", id).select().single(),
        ),
      );
      const error = results.find((r) => r.error);
      if (error?.error) throw error.error;
      return results.map((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scoring_weights"] });
      toast.success("Scoring weights saved");
    },
    onError: (e) => toast.error(`Failed to save weights: ${e.message}`),
  });
};
